/**
 * Type definitions for Agencies.
 * These interfaces, aliases, or model helpers describe the shared data contract used
 * by the surrounding feature or UI layer.
 */
import { AddressDetails, DEFAULT_ADDRESS_DETAILS, GeoCoordinates } from '../../shared/models/address.types';
import { ContactDetails, ContactPhone, DEFAULT_CONTACT_PHONE } from '../../shared/models/contact.types';

export type AgencyStatus = 'active' | 'inactive';
export type AgencyProfile = 'point-of-connect' | 'sales-administration-office' | 'mini-service-container';
export type AgencyStockStatus = 'ok' | 'low' | 'out-of-stock';

export type AgencyPhone = ContactPhone;
export type AgencyContact = ContactDetails;
export type AgencyGeoCoordinates = GeoCoordinates;
export type AgencyAddress = AddressDetails;

export interface AgencyAssignedUserSummary {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  status?: string;
}

export interface AgencyAssignedPosSummary {
  id: string;
  name: string;
  city?: string;
  area?: string;
  status?: string;
  stockStatus?: AgencyStockStatus;
  assignedAgentName?: string;
  primaryAgent?: string;
}

export interface AgencyModel {
  id: string;
  name: string;
  code: string;
  city: string;
  area: string;
  address: string;
  contact: string;
  status: AgencyStatus;
  profile?: string;
  profileName?: string;
  stockStatus: AgencyStockStatus;
  supervisors: number;
  agents: number;
  posCount: number;
  agencyChief?: string;
  officeNumber?: string;
  posNumber?: string;
  contactDetails: AgencyContact;
  addressDetails: AgencyAddress;
  assignedSupervisors: string[];
  assignedSupervisorDetails?: AgencyAssignedUserSummary[];
  assignedSecondarySupervisors: string[];
  assignedSecondarySupervisorDetails?: AgencyAssignedUserSummary[];
  assignedAgents: string[];
  assignedAgentDetails?: AgencyAssignedUserSummary[];
  assignedSecondaryAgents: string[];
  assignedSecondaryAgentDetails?: AgencyAssignedUserSummary[];
  assignedBtlUsers: string[];
  assignedBtlUserDetails?: AgencyAssignedUserSummary[];
  assignedSecondaryBtlUsers: string[];
  assignedSecondaryBtlUserDetails?: AgencyAssignedUserSummary[];
  assignedLogisticsUsers: string[];
  assignedLogisticsUserDetails?: AgencyAssignedUserSummary[];
  assignedSecondaryLogisticsUsers: string[];
  assignedSecondaryLogisticsUserDetails?: AgencyAssignedUserSummary[];
  assignedPos: string[];
  assignedPosDetails?: AgencyAssignedPosSummary[];
}

export interface AgencyUpsertPayload {
  name: string;
  code: string;
  profile?: string;
  agencyChief?: string;
  officeNumber?: string;
  posNumber?: string;
  contact: AgencyContact;
  address: AgencyAddress;
  assignedSupervisors?: string[];
  assignedSecondarySupervisors?: string[];
  assignedAgents?: string[];
  assignedSecondaryAgents?: string[];
  assignedBtlUsers?: string[];
  assignedSecondaryBtlUsers?: string[];
  assignedLogisticsUsers?: string[];
  assignedSecondaryLogisticsUsers?: string[];
  assignedPos?: string[];
}

export interface AgencyRecord {
  id: string;
  name: string;
  code: string;
  city: string;
  area: string;
  address: string;
  contact: string;
  status: AgencyStatus;
  profile?: string;
  profileName?: string;
  stockStatus: AgencyStockStatus;
  supervisors: number;
  agents: number;
  posCount: number;
  agencyChief?: string;
  officeNumber?: string;
  posNumber?: string;
  contactDetails?: AgencyContact;
  addressDetails?: AgencyAddress;
  assignedSupervisors?: string[];
  assignedSupervisorDetails?: AgencyAssignedUserSummary[];
  assignedSecondarySupervisors?: string[];
  assignedSecondarySupervisorDetails?: AgencyAssignedUserSummary[];
  assignedAgents?: string[];
  assignedAgentDetails?: AgencyAssignedUserSummary[];
  assignedSecondaryAgents?: string[];
  assignedSecondaryAgentDetails?: AgencyAssignedUserSummary[];
  assignedBtlUsers?: string[];
  assignedBtlUserDetails?: AgencyAssignedUserSummary[];
  assignedSecondaryBtlUsers?: string[];
  assignedSecondaryBtlUserDetails?: AgencyAssignedUserSummary[];
  assignedLogisticsUsers?: string[];
  assignedLogisticsUserDetails?: AgencyAssignedUserSummary[];
  assignedSecondaryLogisticsUsers?: string[];
  assignedSecondaryLogisticsUserDetails?: AgencyAssignedUserSummary[];
  assignedPos?: string[];
  assignedPosDetails?: AgencyAssignedPosSummary[];
}

export const DEFAULT_PHONE: AgencyPhone = DEFAULT_CONTACT_PHONE;
export const DEFAULT_ADDRESS: AgencyAddress = DEFAULT_ADDRESS_DETAILS;

export interface AgencyProfileConfiguration {
  id: string;
  name: string;
  createdAt?: string;
  updatedAt?: string;
}

export function toAgencyRecord(id: string, payload: AgencyUpsertPayload, current?: AgencyRecord): AgencyRecord {
  const flattened = flattenAgencyPayload(payload);

  return {
    id,
    status: current?.status ?? 'active',
    stockStatus: current?.stockStatus ?? 'ok',
    supervisors: current?.supervisors ?? 0,
    agents: current?.agents ?? 0,
    posCount: current?.posCount ?? 0,
    assignedSupervisors: payload.assignedSupervisors ?? current?.assignedSupervisors ?? [],
    assignedSupervisorDetails: current?.assignedSupervisorDetails ?? [],
    assignedSecondarySupervisors: payload.assignedSecondarySupervisors ?? current?.assignedSecondarySupervisors ?? [],
    assignedSecondarySupervisorDetails: current?.assignedSecondarySupervisorDetails ?? [],
    assignedAgents: payload.assignedAgents ?? current?.assignedAgents ?? [],
    assignedAgentDetails: current?.assignedAgentDetails ?? [],
    assignedSecondaryAgents: payload.assignedSecondaryAgents ?? current?.assignedSecondaryAgents ?? [],
    assignedSecondaryAgentDetails: current?.assignedSecondaryAgentDetails ?? [],
    assignedBtlUsers: payload.assignedBtlUsers ?? current?.assignedBtlUsers ?? [],
    assignedBtlUserDetails: current?.assignedBtlUserDetails ?? [],
    assignedSecondaryBtlUsers: payload.assignedSecondaryBtlUsers ?? current?.assignedSecondaryBtlUsers ?? [],
    assignedSecondaryBtlUserDetails: current?.assignedSecondaryBtlUserDetails ?? [],
    assignedLogisticsUsers: payload.assignedLogisticsUsers ?? current?.assignedLogisticsUsers ?? [],
    assignedLogisticsUserDetails: current?.assignedLogisticsUserDetails ?? [],
    assignedSecondaryLogisticsUsers: payload.assignedSecondaryLogisticsUsers ?? current?.assignedSecondaryLogisticsUsers ?? [],
    assignedSecondaryLogisticsUserDetails: current?.assignedSecondaryLogisticsUserDetails ?? [],
    assignedPos: payload.assignedPos ?? current?.assignedPos ?? [],
    assignedPosDetails: current?.assignedPosDetails ?? [],
    ...flattened
  };
}

export function toAgencyModel(record: AgencyRecord): AgencyModel {
  return {
    ...record,
    contactDetails: record.contactDetails ?? parseLegacyContact(record.contact),
    addressDetails: record.addressDetails ?? parseLegacyAddress(record),
    assignedSupervisors: record.assignedSupervisors ?? [],
    assignedSupervisorDetails: record.assignedSupervisorDetails ?? [],
    assignedSecondarySupervisors: record.assignedSecondarySupervisors ?? [],
    assignedSecondarySupervisorDetails: record.assignedSecondarySupervisorDetails ?? [],
    assignedAgents: record.assignedAgents ?? [],
    assignedAgentDetails: record.assignedAgentDetails ?? [],
    assignedSecondaryAgents: record.assignedSecondaryAgents ?? [],
    assignedSecondaryAgentDetails: record.assignedSecondaryAgentDetails ?? [],
    assignedBtlUsers: record.assignedBtlUsers ?? [],
    assignedBtlUserDetails: record.assignedBtlUserDetails ?? [],
    assignedSecondaryBtlUsers: record.assignedSecondaryBtlUsers ?? [],
    assignedSecondaryBtlUserDetails: record.assignedSecondaryBtlUserDetails ?? [],
    assignedLogisticsUsers: record.assignedLogisticsUsers ?? [],
    assignedLogisticsUserDetails: record.assignedLogisticsUserDetails ?? [],
    assignedSecondaryLogisticsUsers: record.assignedSecondaryLogisticsUsers ?? [],
    assignedSecondaryLogisticsUserDetails: record.assignedSecondaryLogisticsUserDetails ?? [],
    assignedPos: record.assignedPos ?? [],
    assignedPosDetails: record.assignedPosDetails ?? []
  };
}

export function flattenAgencyPayload(payload: AgencyUpsertPayload): Omit<AgencyRecord, 'id' | 'status' | 'stockStatus' | 'supervisors' | 'agents' | 'posCount'> {
  return {
    name: payload.name,
    code: payload.code,
    profile: payload.profile,
    agencyChief: payload.agencyChief,
    officeNumber: payload.officeNumber,
    posNumber: payload.posNumber,
    city: payload.address.locality,
    area: payload.address.area,
    address: payload.address.formattedAddress,
    contact: payload.contact.phone.number,
    contactDetails: payload.contact,
    addressDetails: payload.address
  };
}

function parseLegacyContact(contact: string): AgencyContact {
  return {
    email: '',
    phone: {
      ...DEFAULT_PHONE,
      number: contact
    }
  };
}

function parseLegacyAddress(record: Pick<AgencyRecord, 'address' | 'city' | 'area'>): AgencyAddress {
  return {
    ...DEFAULT_ADDRESS,
    formattedAddress: record.address,
    locality: record.city,
    area: record.area
  };
}
